
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IProgram, ITournamentEdition, OmneHostService, SanitizedResponse } from "../../../services";
import styles from "../assets/style.module.css";
import { TournamentCard } from "./Tournaments";
import { i18nManager } from "../../../i18n";
import { Utils } from "../../../utils";

const PerfStat = ({ label, value, icon }: any) => (
    <div className={styles["perf-card"]}>
        <div style={{ color: 'var(--accent-color)', fontSize: '24px' }}>{icon}</div>
        <div className={styles["perf-label"]}>{label}</div>
        <div className={styles["perf-value"]}>{value}</div>
    </div>
);

export interface HomeProps {
    program: IProgram;
}

function Home(props: HomeProps) {

    const { program } = props;
    const labels = i18nManager.Labels;
    const omnehostService = OmneHostService.getInstance();
    const payouts = Object.entries(program.payouts).reduce((acc, [currency, amount]) => {
        acc.push({ currency, amount });
        return acc;
    }, [] as { currency: string; amount: number; }[]);
    const [activeSlide, setActiveSlide] = React.useState(0);
    const [tournamentEditions, setTournamentEditions] = React.useState<ApiResponseData<ITournamentEdition>>();

    React.useEffect(() => {
        fetchTournaments();
        const timer = setInterval(() => setActiveSlide(s => (s + 1) % program.banner_urls.length), 5000);
        return () => {
            clearInterval(timer);
        }
    }, []);

    return (<>
        <div className={styles["hero"]}>
            {program.banner_urls.map((img, idx) => (
                <div
                    key={idx}
                    className={`${styles["slide"]} ${activeSlide === idx ? styles["active"] : ''}`}
                >
                    <img src={img} alt="Hero" />
                </div>
            ))}
            <div className={styles["hero-overlay"]}>
                <h1 style={{ fontSize: '72px', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', margin: 0, lineHeight: 0.9 }}>
                    {program.full_name} <br /><span style={{ color: 'var(--accent-color)' }}>({program.short_name})</span>
                </h1>
                <p style={{ color: 'var(--text-secondary)', maxWidth: '500px', margin: '24px 0 40px 0', fontSize: '17px' }}>{program.description}</p>
                <div className={styles["platform-container"]}>
                    {(program.platforms?.length ? program.platforms : ["All Platforms"]).map((platform) => (<div key={platform} className={styles["platform-chip"]}>{platform}</div>))}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                    <ReactRouterDom.NavLink end to={`${window.location.origin}${window.location.pathname}/tournaments`} className={styles["btn-primary"]} style={{ width: 'fit-content', marginTop: 30, marginRight: 30 }}>{labels.program.home.search_tournaments}</ReactRouterDom.NavLink>
                    <ReactRouterDom.NavLink target="_blank" to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/hosts`} className={styles["btn-primary"]} style={{ width: 'fit-content', marginTop: 30, marginRight: 30 }}>{labels.program.home.create_tournaments}</ReactRouterDom.NavLink>
                </div>
            </div>
        </div>

        <div className={styles["container"]}>
            <div className={styles["perf-grid"]}>
                <PerfStat icon="🔱" label={labels.program.home.stats.total_tournaments} value={program.stats.no_of_tournaments?.toLocaleString()} />
                <PerfStat icon="⚔️" label={labels.program.home.stats.active_teams} value={program.stats.no_of_teams.toLocaleString()} />
                <PerfStat icon="👥" label={labels.program.home.stats.total_members} value={program.stats.no_of_members.toLocaleString()} />
                <PerfStat icon="⚡" label={labels.program.home.stats.total_matches} value={program.stats.no_of_matches.toLocaleString()} />
                <PerfStat icon="🏆" label={labels.program.home.stats.trophies_issued} value={program.stats.no_of_trophies.toLocaleString()} />
                <PerfStat icon="🎁" label={labels.program.home.stats.physical_rewards} value={program.stats.no_of_physical_rewards.toLocaleString()} />
            </div>

            <h2 style={{ fontStyle: 'italic', fontSize: '36px', textTransform: 'uppercase', fontWeight: 900, marginBottom: '40px' }}>
                {labels.program.home.payouts}
            </h2>
            <div className={styles["stats-grid"]}>
                {payouts.map((payout) => (<div key={payout.currency} className={styles["stat-card"]}>
                    <div className={styles["stat-label"]}>{payout.currency} Paid</div>
                    <div className={styles["stat-value"]}>{i18nManager.queryCountryCurrency(payout.currency, "currency_code", "currency_sign",)} {payout.amount.toLocaleString()}</div>
                    <div style={{ color: 'var(--accent-color)', fontSize: '11px', marginTop: '10px', fontWeight: '800', textTransform: 'uppercase' }}>
                        {i18nManager.queryCountryCurrency(payout.currency, "currency_code", "currency_name")} {labels.program.home.pool}
                    </div>
                </div>))}
            </div>

            <h2 style={{ fontStyle: 'italic', fontSize: '36px', textTransform: 'uppercase', fontWeight: 900, marginBottom: '40px' }}>
                {labels.program.home.latest_tournaments}
            </h2>
            {(!!tournamentEditions)
                ? ((tournamentEditions.content && tournamentEditions.content?.length > 0)
                    ? (<div className={styles["tournament-grid"]}>
                        {tournamentEditions.content!.map((t) => <TournamentCard key={t.external_id} t={t} />)}
                    </div>)
                    : ("Empty"))
                : (Utils.loadingState())}
        </div>
    </>);

    function fetchTournaments() {
        omnehostService.queryTournamentEditions({
            limit: 60,
            sort: "status,DESC",
            program_id: program.external_id,
            status__in: ["UPCOMING", "ONGOING", "ENDED"],
        }).then(async ({ apiResponse }: SanitizedResponse<ITournamentEdition>) => {
            setTournamentEditions(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Home;